import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { recibosCobroAPI, usuariosAPI, cuentasBancariasAPI } from '../services/api';
import { toast } from 'react-toastify';
import { SelectorColumnas } from '../components/SelectorColumnas';
import { SelectorFiltros } from '../components/SelectorFiltros';
import { useZonaHoraria } from '../context/ZonaHorariaContext';
import { useBranding } from '../context/BrandingContext';
import { FormasPagoDetalleList } from '../components/FormasPagoDetalleList';
import { cargarFormasRecibo, cargarFormasPago } from '../utils/cargarFormasComprobante';
import { imprimirListadoChequesEmitidos, escHtml, valorCeldaEmitidosExport } from '../utils/impresionCheques';
import { ExportActions } from '../components/ExportActions';
import { exportFromColumnas } from '../utils/exportListado';
import { loadFiltrosSession, saveFiltrosSession } from '../utils/filtrosSession';
import './ChequesEmitidos.css';

const MESES_ES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const CLAVE_PREFERENCIAS = 'cheques_emitidos_listado';
const CLAVE_FILTROS = 'cheques_emitidos_listado_filtros';
const COLUMNAS_DEFINICION = [
  { id: 'fecha', label: 'Fecha' },
  { id: 'cliente', label: 'Cliente' },
  { id: 'numero', label: 'Nº cheque' },
  { id: 'f_cobro', label: 'F. cobro' },
  { id: 'importe', label: 'Importe' },
  { id: 'cuenta_banco', label: 'Cuenta / Banco' },
  { id: 'electronico', label: 'Electrónico' },
  { id: 'estado', label: 'Estado' },
  { id: 'comprobantes', label: 'Comprobantes' },
];
const defaultColumnasVisibles = COLUMNAS_DEFINICION.reduce((acc, c) => ({ ...acc, [c.id]: true }), {});
const defaultFiltrosVisibles = COLUMNAS_DEFINICION.reduce((acc, c) => ({ ...acc, [c.id]: true }), {});
const defaultOrdenColumnas = COLUMNAS_DEFINICION.map(c => c.id);
const defaultFiltrosValores = {
  cliente: '',
  numero: '',
  fecha_cobro_desde: '',
  fecha_cobro_hasta: '',
  importe_min: '',
  cuenta_banco_ids: [],
  electronico: '',
  estado: '',
};

const ChequesEmitidos = () => {
  const { formatFecha } = useZonaHoraria();
  const { logoUrl } = useBranding();
  const [chequesAll, setChequesAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detalleCheque, setDetalleCheque] = useState(null);
  const [detalleFormas, setDetalleFormas] = useState({ formas: [], loading: false });
  const [cuentasBancarias, setCuentasBancarias] = useState([]);
  const [filtroCuentaBancoAbierto, setFiltroCuentaBancoAbierto] = useState(false);
  const refFiltroCuentaBanco = useRef(null);
  const [filtrosValores, setFiltrosValores] = useState(() => loadFiltrosSession(CLAVE_FILTROS, defaultFiltrosValores));
  const [columnasVisibles, setColumnasVisibles] = useState(() => ({ ...defaultColumnasVisibles }));
  const [filtrosVisibles, setFiltrosVisibles] = useState(() => ({ ...defaultFiltrosVisibles }));
  const [ordenColumnas, setOrdenColumnas] = useState(() => [...defaultOrdenColumnas]);
  const [sortByColumn, setSortByColumn] = useState(null);
  const [sortAsc, setSortAsc] = useState(true);
  const [, setLoadingPreferencias] = useState(true);
  const [savingPreferencias, setSavingPreferencias] = useState(false);

  useEffect(() => {
    saveFiltrosSession(CLAVE_FILTROS, filtrosValores);
  }, [filtrosValores]);

  const colVisible = (id) => columnasVisibles[id] !== false;
  const getSortValueEmitidos = (ch, colId) => {
    switch (colId) {
      case 'fecha': return new Date(ch.recibo_fecha || ch.fecha_emision || 0).getTime() || 0;
      case 'cliente': return (ch.cliente_razon_social || '').toString().toLowerCase();
      case 'numero': return (ch.numero || '').toString().toLowerCase();
      case 'f_cobro': return new Date(ch.fecha_cobro || 0).getTime() || 0;
      case 'importe': return parseFloat(ch.importe || 0) || 0;
      case 'cuenta_banco': return [(ch.cuenta_numero || ''), (ch.cuenta_banco_nombre || '')].join(' ').toLowerCase();
      case 'electronico': return ch.es_electronico ? 1 : 0;
      case 'estado': return (ch.estado || '').toString().toLowerCase();
      case 'comprobantes': return (ch.origen || '').toString().toLowerCase();
      default: return '';
    }
  };
  const filtroVisible = useCallback((colId) => filtrosVisibles[colId] !== false && columnasVisibles[colId] !== false, [filtrosVisibles, columnasVisibles]);
  const setFiltro = (key, value) => setFiltrosValores(prev => ({ ...prev, [key]: value }));

  const loadCheques = useCallback(async () => {
    try {
      setLoading(true);
      const res = await recibosCobroAPI.getChequesEmitidos();
      if (res.success && Array.isArray(res.data)) {
        setChequesAll(res.data);
      } else {
        setChequesAll([]);
      }
    } catch (error) {
      toast.error(error.message || 'Error al cargar cheques emitidos');
      setChequesAll([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCheques();
  }, [loadCheques]);

  const cheques = useMemo(() => {
    let data = chequesAll;
    if (filtroVisible('cliente') && filtrosValores.cliente?.trim()) {
      const busqueda = String(filtrosValores.cliente).trim().toLowerCase();
      data = data.filter(ch => String(ch.cliente_razon_social || '').toLowerCase().includes(busqueda));
    }
    if (filtroVisible('numero') && filtrosValores.numero?.trim()) {
      const busqueda = String(filtrosValores.numero).trim().toLowerCase();
      data = data.filter(ch => String(ch.numero || '').toLowerCase().includes(busqueda));
    }
    if (filtroVisible('f_cobro') && filtrosValores.fecha_cobro_desde?.trim()) {
      data = data.filter(ch => (ch.fecha_cobro || '') >= filtrosValores.fecha_cobro_desde.trim());
    }
    if (filtroVisible('f_cobro') && filtrosValores.fecha_cobro_hasta?.trim()) {
      data = data.filter(ch => (ch.fecha_cobro || '') <= filtrosValores.fecha_cobro_hasta.trim());
    }
    if (filtroVisible('importe') && filtrosValores.importe_min !== '') {
      const min = parseFloat(filtrosValores.importe_min);
      if (!isNaN(min)) data = data.filter(ch => parseFloat(ch.importe || 0) >= min);
    }
    if (filtroVisible('cuenta_banco') && Array.isArray(filtrosValores.cuenta_banco_ids) && filtrosValores.cuenta_banco_ids.length > 0) {
      const ids = filtrosValores.cuenta_banco_ids.map(id => Number(id));
      data = data.filter(ch => ch.cuenta_bancaria_id != null && ids.includes(Number(ch.cuenta_bancaria_id)));
    }
    if (filtroVisible('electronico') && filtrosValores.electronico) {
      if (filtrosValores.electronico === 'Sí') data = data.filter(ch => ch.es_electronico);
      if (filtrosValores.electronico === 'No') data = data.filter(ch => !ch.es_electronico);
    }
    if (filtroVisible('estado') && filtrosValores.estado) {
      data = data.filter(ch => (ch.estado || 'emitido').toLowerCase() === filtrosValores.estado.toLowerCase());
    }
    return data;
  }, [chequesAll, filtrosValores, filtroVisible]);

  useEffect(() => {
    if (!detalleCheque) {
      setDetalleFormas({ formas: [], loading: false });
      return;
    }
    let cancelled = false;
    (async () => {
      setDetalleFormas({ formas: [], loading: true });
      const comp = detalleCheque.comprobante_origen;
      const formas = comp?.tipo === 'Orden de pago'
        ? await cargarFormasPago(comp)
        : await cargarFormasRecibo(comp);
      if (!cancelled) setDetalleFormas({ formas, loading: false });
    })();
    return () => { cancelled = true; };
  }, [detalleCheque]);

  useEffect(() => {
    let cancelled = false;
    cuentasBancariasAPI.getAll({ activo: true }).then((res) => {
      if (cancelled) return;
      setCuentasBancarias(Array.isArray(res.data) ? res.data : []);
    }).catch(() => { if (!cancelled) setCuentasBancarias([]); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (refFiltroCuentaBanco.current && !refFiltroCuentaBanco.current.contains(e.target)) {
        setFiltroCuentaBancoAbierto(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingPreferencias(true);
      try {
        const res = await usuariosAPI.getPreferencias(CLAVE_PREFERENCIAS);
        if (cancelled) return;
        const raw = Array.isArray(res.data) ? res.data[0] : res.data;
        const data = raw?.valor ?? null;
        if (data && typeof data === 'object') {
          if (data.columnasVisibles && typeof data.columnasVisibles === 'object') {
            setColumnasVisibles(prev => ({ ...defaultColumnasVisibles, ...data.columnasVisibles }));
          }
          if (data.filtrosVisibles && typeof data.filtrosVisibles === 'object') {
            setFiltrosVisibles(prev => ({ ...defaultFiltrosVisibles, ...data.filtrosVisibles }));
          }
          if (data.ordenColumnas && Array.isArray(data.ordenColumnas) && data.ordenColumnas.length) {
            const saved = data.ordenColumnas.filter(id => defaultOrdenColumnas.includes(id));
            const nuevos = defaultOrdenColumnas.filter(id => !saved.includes(id));
            setOrdenColumnas([...saved, ...nuevos]);
          }
        }
      } catch (e) {
        if (!cancelled) console.error('Error al cargar preferencias:', e);
      } finally {
        if (!cancelled) setLoadingPreferencias(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const guardarPreferenciasListado = useCallback(async () => {
    setSavingPreferencias(true);
    try {
      await usuariosAPI.savePreferencias(CLAVE_PREFERENCIAS, {
        columnasVisibles: { ...columnasVisibles },
        filtrosVisibles: { ...filtrosVisibles },
        ordenColumnas: [...ordenColumnas],
      });
      toast.success('Configuración guardada. Se aplica solo para su usuario.');
    } catch (e) {
      toast.error(e.message || 'Error al guardar configuración');
    } finally {
      setSavingPreferencias(false);
    }
  }, [columnasVisibles, filtrosVisibles, ordenColumnas]);

  const ordenColumnasVisible = useMemo(
    () => ordenColumnas.filter(id => colVisible(id)),
    [ordenColumnas, columnasVisibles]
  );

  const chequesOrdenados = useMemo(() => {
    if (!sortByColumn) return cheques;
    const arr = [...cheques];
    arr.sort((a, b) => {
      const v1 = getSortValueEmitidos(a, sortByColumn);
      const v2 = getSortValueEmitidos(b, sortByColumn);
      const num = typeof v1 === 'number' && typeof v2 === 'number';
      const cmp = num ? v1 - v2 : String(v1).localeCompare(String(v2), 'es');
      return sortAsc ? cmp : -cmp;
    });
    return arr;
  }, [cheques, sortByColumn, sortAsc]);

  const handleSortColumn = (colId) => {
    if (sortByColumn === colId) setSortAsc(prev => !prev);
    else { setSortByColumn(colId); setSortAsc(true); }
  };

  const getColumnLabel = (colId) => COLUMNAS_DEFINICION.find(c => c.id === colId)?.label || colId;

  const generarPDF = () => {
    if (!chequesOrdenados.length) {
      toast.info('No hay cheques para imprimir con los filtros actuales');
      return;
    }
    const fechaReporte = new Date();
    const fechaReporteStr = `${fechaReporte.getDate()} de ${MESES_ES[fechaReporte.getMonth()]} de ${fechaReporte.getFullYear()}`;
    const partesFiltro = [];
    if (filtrosValores.cliente?.trim()) partesFiltro.push(`<p><strong>Cliente:</strong> ${escHtml(filtrosValores.cliente.trim())}</p>`);
    if (filtrosValores.numero?.trim()) partesFiltro.push(`<p><strong>Nº cheque:</strong> ${escHtml(filtrosValores.numero.trim())}</p>`);
    if (filtrosValores.fecha_cobro_desde || filtrosValores.fecha_cobro_hasta) {
      partesFiltro.push(`<p><strong>F. cobro:</strong> ${filtrosValores.fecha_cobro_desde || '...'} a ${filtrosValores.fecha_cobro_hasta || '...'}</p>`);
    }
    if (filtrosValores.importe_min !== '') {
      partesFiltro.push(`<p><strong>Importe mín.:</strong> $${parseFloat(filtrosValores.importe_min || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</p>`);
    }
    if (Array.isArray(filtrosValores.cuenta_banco_ids) && filtrosValores.cuenta_banco_ids.length > 0) {
      const nombres = filtrosValores.cuenta_banco_ids.map((id) => {
        const cb = cuentasBancarias.find((c) => Number(c.id) === Number(id));
        return cb ? `${cb.numero_cuenta || cb.nombre}${cb.banco_nombre ? ` (${cb.banco_nombre})` : ''}` : id;
      });
      partesFiltro.push(`<p><strong>Cuenta / Banco:</strong> ${escHtml(nombres.join(', '))}</p>`);
    }
    if (filtrosValores.electronico) partesFiltro.push(`<p><strong>Electrónico:</strong> ${escHtml(filtrosValores.electronico)}</p>`);
    if (filtrosValores.estado) partesFiltro.push(`<p><strong>Estado:</strong> ${escHtml(filtrosValores.estado)}</p>`);
    try {
      imprimirListadoChequesEmitidos({
        cheques: chequesOrdenados,
        columnasDefinicion: COLUMNAS_DEFINICION,
        columnasVisiblesIds: ordenColumnasVisible,
        formatFecha,
        filtrosHtml: partesFiltro.join(''),
        logoUrl,
        subtitulo: `Reporte generado el ${fechaReporteStr}`,
      });
    } catch (e) {
      toast.error(e.message || 'Error al imprimir');
    }
  };

  const handleExportListado = async (format) => {
    try {
      await exportFromColumnas({
        filename: 'cheques_emitidos',
        sheetName: 'Cheques',
        columnDefs: COLUMNAS_DEFINICION,
        visibleColumnIds: ordenColumnasVisible,
        items: chequesOrdenados,
        getCellValue: (ch, colId) => valorCeldaEmitidosExport(ch, colId, formatFecha),
        format,
      });
    } catch (e) {
      toast.error(e.message || 'Error al exportar');
    }
  };

  if (loading) {
    return <div className="loading">Cargando...</div>;
  }

  return (
    <div className="clientes pagina-listado pagina-listado--scroll">
      <div className="page-header">
        <h1>Cheques emitidos</h1>
        {chequesOrdenados.length > 0 && (
          <div className="page-header-acciones">
            <ExportActions
              onCsv={() => handleExportListado('csv')}
              onExcel={() => handleExportListado('excel')}
              onPrint={generarPDF}
              printLabel="Imprimir listado"
            />
          </div>
        )}
      </div>

      <div className="listado-panel clientes-listado listado-controles">
      <div className="filters" style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 0 }}>
            {filtroVisible('cliente') && (
              <input
                type="text"
                placeholder="Cliente"
                value={filtrosValores.cliente}
                onChange={(e) => setFiltro('cliente', e.target.value)}
                style={{ minWidth: 120, padding: '8px 10px' }}
              />
            )}
            {filtroVisible('numero') && (
              <input
                type="text"
                placeholder="Nº cheque"
                value={filtrosValores.numero}
                onChange={(e) => setFiltro('numero', e.target.value)}
                style={{ width: 120, padding: '8px 10px' }}
              />
            )}
            {filtroVisible('f_cobro') && (
              <>
                <input
                  type="date"
                  value={filtrosValores.fecha_cobro_desde}
                  onChange={(e) => setFiltro('fecha_cobro_desde', e.target.value)}
                  style={{ width: 140, padding: '8px 10px' }}
                  title="Fecha cobro desde"
                />
                <input
                  type="date"
                  value={filtrosValores.fecha_cobro_hasta}
                  onChange={(e) => setFiltro('fecha_cobro_hasta', e.target.value)}
                  style={{ width: 140, padding: '8px 10px' }}
                  title="Fecha cobro hasta"
                />
              </>
            )}
            {filtroVisible('importe') && (
              <input
                type="number"
                placeholder="Importe mín."
                value={filtrosValores.importe_min}
                onChange={(e) => setFiltro('importe_min', e.target.value)}
                step="0.01"
                min="0"
                style={{ width: 120, padding: '8px 10px' }}
              />
            )}
            {filtroVisible('cuenta_banco') && (
              <div className="filtro filtro-cuentas-banco-checkbox" ref={refFiltroCuentaBanco}>
                <label>Cuenta / Banco</label>
                <button
                  type="button"
                  className={`selector-cuentas-banco-trigger ${filtroCuentaBancoAbierto ? 'abierto' : ''}`}
                  onClick={() => setFiltroCuentaBancoAbierto((a) => !a)}
                  aria-expanded={filtroCuentaBancoAbierto}
                  aria-haspopup="listbox"
                >
                  {!filtrosValores.cuenta_banco_ids?.length
                    ? 'Todas'
                    : filtrosValores.cuenta_banco_ids.length === 1
                      ? (() => {
                          const c = cuentasBancarias.find((cb) => Number(cb.id) === Number(filtrosValores.cuenta_banco_ids[0]));
                          return c ? `${c.numero_cuenta || c.nombre || 'Cuenta'}${c.banco_nombre ? ` (${c.banco_nombre})` : ''}` : '1 cuenta';
                        })()
                      : `${filtrosValores.cuenta_banco_ids.length} cuentas`}
                  <span className="selector-cuentas-banco-chevron" aria-hidden>▼</span>
                </button>
                {filtroCuentaBancoAbierto && (
                  <div className="selector-cuentas-banco-panel" role="listbox">
                    <div className="selector-cuentas-banco-acciones">
                      <button type="button" className="selector-cuentas-banco-btn-todo" onClick={() => setFiltro('cuenta_banco_ids', cuentasBancarias.map((c) => c.id))}>
                        Seleccionar todo
                      </button>
                      <button type="button" className="selector-cuentas-banco-btn-todo" onClick={() => setFiltro('cuenta_banco_ids', [])}>
                        Deseleccionar todo
                      </button>
                    </div>
                    {cuentasBancarias.map((cb) => (
                      <label key={cb.id} className="selector-cuentas-banco-opcion">
                        <input
                          type="checkbox"
                          checked={(filtrosValores.cuenta_banco_ids || []).some((id) => Number(id) === Number(cb.id))}
                          onChange={() => {
                            const ids = filtrosValores.cuenta_banco_ids || [];
                            const nuevo = ids.some((id) => Number(id) === Number(cb.id))
                              ? ids.filter((x) => Number(x) !== Number(cb.id))
                              : [...ids, cb.id];
                            setFiltro('cuenta_banco_ids', nuevo);
                          }}
                        />
                        <span>{cb.numero_cuenta || cb.nombre || `Cuenta ${cb.id}`}{cb.banco_nombre ? ` (${cb.banco_nombre})` : ''}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
            {filtroVisible('electronico') && (
              <select value={filtrosValores.electronico} onChange={(e) => setFiltro('electronico', e.target.value)}>
                <option value="">Electrónico: Todos</option>
                <option value="Sí">Sí</option>
                <option value="No">No</option>
              </select>
            )}
            {filtroVisible('estado') && (
              <select value={filtrosValores.estado} onChange={(e) => setFiltro('estado', e.target.value)}>
                <option value="">Todos los estados</option>
                <option value="emitido">Emitido</option>
                <option value="presentado">Presentado</option>
                <option value="pagado">Pagado</option>
                <option value="devuelto">Devuelto</option>
                <option value="vencido">Vencido</option>
              </select>
            )}
            <button type="button" className="btn-secondary" onClick={() => setFiltrosValores({ ...defaultFiltrosValores })} title="Limpiar todos los filtros">
              Limpiar filtros
            </button>
            <SelectorColumnas
              columnas={COLUMNAS_DEFINICION}
              visibleIds={ordenColumnas.filter(id => columnasVisibles[id] !== false)}
              onToggle={(id) => setColumnasVisibles(prev => ({ ...prev, [id]: !prev[id] }))}
              onRestaurar={() => setColumnasVisibles({ ...defaultColumnasVisibles })}
              onSave={guardarPreferenciasListado}
              ordenColumnas={ordenColumnas}
              onOrdenChange={setOrdenColumnas}
              titulo="Columnas visibles"
              labelBoton="Columnas"
            />
            <SelectorFiltros
              filtros={COLUMNAS_DEFINICION}
              visibleIds={Object.keys(filtrosVisibles).filter(id => filtrosVisibles[id] !== false)}
              onToggle={(id) => setFiltrosVisibles(prev => ({ ...prev, [id]: !prev[id] }))}
              onRestaurar={() => setFiltrosVisibles({ ...defaultFiltrosVisibles })}
              onSave={guardarPreferenciasListado}
              titulo="Filtros visibles"
              labelBoton="Filtros"
            />
          </div>

      <div className="listado-panel-cuerpo clientes-listado-cuerpo">
      <div className="listado-tabla-area clientes-listado-tabla-area">
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  {ordenColumnasVisible.map(colId => (
                    <th
                      key={colId}
                      className="th-sortable"
                      onClick={() => handleSortColumn(colId)}
                      title={`Ordenar por ${getColumnLabel(colId)}`}
                    >
                      <span>{getColumnLabel(colId)}</span>
                      {sortByColumn === colId && (
                        <span className="sort-indicator" aria-hidden="true">
                          {sortAsc ? ' ▲' : ' ▼'}
                        </span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {chequesOrdenados.length === 0 ? (
                  <tr>
                    <td colSpan={ordenColumnasVisible.length || 1} className="no-data">
                      No hay cheques emitidos que coincidan con el filtro
                    </td>
                  </tr>
                ) : (
                  chequesOrdenados.map((ch) => (
                    <tr key={ch.id}>
                      {ordenColumnasVisible.map(colId => {
                        if (colId === 'fecha') return <td key={colId}>{formatFecha(ch.recibo_fecha)}</td>;
                        if (colId === 'cliente') return <td key={colId}>{ch.cliente_razon_social || '-'}</td>;
                        if (colId === 'numero') return <td key={colId}>{ch.numero || '-'}</td>;
                        if (colId === 'f_cobro') return <td key={colId}>{formatFecha(ch.fecha_cobro)}</td>;
                        if (colId === 'importe') return <td key={colId} style={{ textAlign: 'right' }}>${parseFloat(ch.importe || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>;
                        if (colId === 'cuenta_banco') return <td key={colId}>{(ch.cuenta_numero && ch.cuenta_banco_nombre) ? `${ch.cuenta_numero} (${ch.cuenta_banco_nombre})` : '-'}</td>;
                        if (colId === 'electronico') return <td key={colId}>{ch.es_electronico ? 'Sí' : 'No'}</td>;
                        if (colId === 'estado') return <td key={colId}><span className="badge estado-cheque">{ch.estado || 'emitido'}</span></td>;
                        if (colId === 'comprobantes') return (
                          <td key={colId}>
                            <button type="button" className="btn-secondary" style={{ padding: '6px 10px', fontSize: '12px' }} onClick={() => setDetalleCheque(ch)}>
                              Ver detalle
                            </button>
                          </td>
                        );
                        return <td key={colId}>-</td>;
                      })}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
      </div>
      </div>
      </div>

      {detalleCheque && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
          onClick={() => setDetalleCheque(null)}
        >
          <div
            style={{
              background: 'white', borderRadius: '8px', padding: '20px', maxWidth: '480px', width: '95%',
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)', maxHeight: '90vh', overflow: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0 }}>Comprobante del cheque</h3>
              <button type="button" onClick={() => setDetalleCheque(null)} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            <p style={{ fontSize: '13px', color: '#555', marginBottom: '12px' }}>
              Nº {detalleCheque.numero} · ${parseFloat(detalleCheque.importe || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
              {' · '}{detalleCheque.proveedor_razon_social || detalleCheque.cliente_razon_social || '-'}
            </p>
            {detalleCheque.comprobante_origen && (
              <div style={{ fontSize: '14px', padding: '10px', background: '#f8f9fa', borderRadius: '6px', marginBottom: '12px' }}>
                <div><strong>{detalleCheque.comprobante_origen.tipo}</strong></div>
                {detalleCheque.comprobante_origen.proveedor_razon_social && (
                  <div style={{ marginTop: '4px' }}><strong>Proveedor:</strong> {detalleCheque.comprobante_origen.proveedor_razon_social}</div>
                )}
                {detalleCheque.comprobante_origen.cliente_razon_social && (
                  <div style={{ marginTop: '4px' }}><strong>Cliente:</strong> {detalleCheque.comprobante_origen.cliente_razon_social}</div>
                )}
                <div>{detalleCheque.comprobante_origen.concepto || '-'}</div>
                <div style={{ color: '#666', marginTop: '4px' }}>Fecha: {formatFecha(detalleCheque.comprobante_origen.fecha)}</div>
                {detalleCheque.comprobante_origen.numero_recibo && (
                  <div style={{ color: '#666' }}>N° recibo: {detalleCheque.comprobante_origen.numero_recibo}</div>
                )}
                {(detalleCheque.comprobante_origen.numero ?? detalleCheque.comprobante_origen.numero_pago_cuenta ?? detalleCheque.comprobante_origen.numero_pago) != null && (
                  <div style={{ color: '#666' }}>
                    {detalleCheque.comprobante_origen.tipo === 'Pago a cuenta' ? 'N° pago a cuenta' : 'N° orden de pago'}: {detalleCheque.comprobante_origen.numero ?? detalleCheque.comprobante_origen.numero_pago_cuenta ?? detalleCheque.comprobante_origen.numero_pago}
                  </div>
                )}
              </div>
            )}
            <FormasPagoDetalleList
              formasPago={detalleFormas.formas}
              titulo="Formas de pago del comprobante"
              loading={detalleFormas.loading}
              formatFecha={formatFecha}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ChequesEmitidos;
